import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
	createMigrate,
	FLUSH,
	PAUSE,
	PERSIST,
	persistReducer,
	persistStore,
	PURGE,
	REGISTER,
	REHYDRATE,
} from "redux-persist";
import storageImport from "redux-persist/lib/storage";
import alertReducer from "./alertSlice";
import dataGridPrefsReducer from "./dataGridPrefsSlice";
import { migrations } from "./migrations";
import navigationReducer from "./navigationSlice";
import pageHeaderReducer from "./pageHeaderSlice";
import userReducer from "./userSlice";

const hasPersistGetItem = (x: unknown): x is typeof storageImport =>
	typeof (x as { getItem?: unknown })?.getItem === "function";

/** CJS default interop (Vite/Rolldown): import may be `{ default: engine }`. */
const nestedDefault = (storageImport as { default?: unknown }).default;
const storage = hasPersistGetItem(storageImport)
	? storageImport
	: hasPersistGetItem(nestedDefault)
		? (nestedDefault as typeof storageImport)
		: (() => {
				throw new Error("redux-persist storage import invalid: missing getItem");
			})();

const persistConfig = {
	key: "root",
	version: 14,
	storage,
	migrate: createMigrate(migrations, { debug: (import.meta as any).env?.VITE_ENVIRONMENT === "dev" ? true : false })
};

const persistedReducer = persistReducer(
	persistConfig,
	combineReducers({
		user: userReducer,
		navigation: navigationReducer,
		dataGridPrefs: dataGridPrefsReducer
	})
);

const rootReducer = persistedReducer;

const store = configureStore({
	reducer: {
		alert: alertReducer,
		pageHeader: pageHeaderReducer,
		root: rootReducer
	},
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const setupStore = (preloadedState?: RootState) => {
	return configureStore({
		reducer: {
			alert: alertReducer,
			pageHeader: pageHeaderReducer,
			root: rootReducer
		},
		preloadedState,
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware({
				serializableCheck: {
					ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
				},
			}),
	});
};

export const persistor = persistStore(store);
export default store;
